import { Info } from "lucide-react"
import { getProviderRequiredHeaders } from "@prescreen/lib/integrations/integration-catalog"
import type { IntegrationProviderId } from "@prescreen/lib/types/integration-provider-id"
import { cn } from "@prescreen/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface IntegrationProviderOptionCardProps {
  providerId: IntegrationProviderId
  title: string
  description: string
  selected: boolean
  onToggle: () => void
}

function ProviderRequestHeadersInfo({ providerId }: { providerId: IntegrationProviderId }) {
  const headers = getProviderRequiredHeaders(providerId)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="absolute top-3 right-3 inline-flex rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={`Request headers for ${providerId}`}
          onClick={(event) => event.stopPropagation()}
        >
          <Info className="size-4" strokeWidth={1.5} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="text-sm font-medium text-gray-900">Request headers</p>
        <p className="mt-1 text-xs leading-snug text-muted-foreground">
          Send these <code className="rounded bg-muted px-1">x-oe-*</code> headers on each API
          request to this service
        </p>
        {headers.length > 0 ? (
          <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-xs">
            {headers.map((header) => (
              <li key={header}>
                <code className="break-all rounded bg-muted px-1 py-0.5">{header}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">No override headers are required.</p>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default function IntegrationProviderOptionCard({
  providerId,
  title,
  description,
  selected,
  onToggle,
}: IntegrationProviderOptionCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-lg border-2 p-4 transition-colors",
        selected
          ? "border-primary bg-primary/5"
          : "border-muted hover:border-muted-foreground/30"
      )}
    >
      <ProviderRequestHeadersInfo providerId={providerId} />
      <button
        type="button"
        aria-pressed={selected}
        onClick={onToggle}
        className="w-full pr-8 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="font-semibold text-gray-900">{title}</div>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
      </button>
    </div>
  )
}
